<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import baileysChannel from 'dashboard/api/channel/baileysChannel';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const STEP_FORM = 'form';
const STEP_QR = 'qr';
const STEP_DONE = 'done';

const step = ref(STEP_FORM);
const inboxName = ref('');
const isCreating = ref(false);
const createdInboxId = ref(null);
const qrDataUrl = ref(null);
const sessionState = ref('connecting');
const phoneConnected = ref(null);
const isPolling = ref(false);
let pollHandle = null;
let pollStartedAt = null;

const account = computed(() => store.getters.getCurrentAccount);

const sessionStateLabel = computed(() => {
  const map = {
    created: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.CREATED'),
    connecting: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.CONNECTING'),
    qr: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.QR'),
    connected: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.CONNECTED'),
    paused: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.PAUSED'),
    disconnected: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.DISCONNECTED'),
    banned: t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.STATE.BANNED'),
  };
  return map[sessionState.value] || sessionState.value;
});

const stopPolling = () => {
  isPolling.value = false;
  if (pollHandle) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
};

const pollStatus = async () => {
  if (!createdInboxId.value) return;
  try {
    const { data } = await baileysChannel.status(createdInboxId.value);
    sessionState.value = data.state;
    phoneConnected.value = data.phone;

    if (data.state === 'qr') {
      try {
        const qrRes = await baileysChannel.qr(createdInboxId.value);
        qrDataUrl.value = qrRes.data.qr_base64;
      } catch (_) {
        // QR endpoint may briefly 404 between regenerations.
      }
    }

    if (data.state === 'connected') {
      stopPolling();
      step.value = STEP_DONE;
    } else if (data.state === 'banned' || data.state === 'disconnected') {
      stopPolling();
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.SESSION_FAILED', { state: data.state }));
    }

    // Safety: stop polling after 5 minutes to avoid burning resources on a forgotten tab.
    if (Date.now() - pollStartedAt > 5 * 60 * 1000) {
      stopPolling();
    }
  } catch (err) {
    // Ignore transient errors - next tick retries.
  }
};

const startPolling = () => {
  pollStartedAt = Date.now();
  isPolling.value = true;
  pollHandle = setInterval(pollStatus, 2000);
  pollStatus();
};

const handleCreate = async () => {
  if (!inboxName.value.trim()) return;
  isCreating.value = true;
  try {
    // Create the inbox via the standard channels endpoint with a placeholder phone.
    // The real phone is filled in by the bridge after QR scan (handled by BaileysEventsJob).
    const placeholderPhone = `+pending${Date.now()}`;
    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp',
        provider: 'baileys',
        phone_number: placeholderPhone,
        provider_config: {},
      },
    });
    createdInboxId.value = response.id;

    // Bridge session is registered automatically via after_commit hook on the channel.
    // Trigger start to get the QR flow going.
    await baileysChannel.start(response.id);

    step.value = STEP_QR;
    startPolling();
  } catch (err) {
    useAlert(err.response?.data?.message || t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CREATE_FAILED'));
  } finally {
    isCreating.value = false;
  }
};

const handleRegenerateQr = async () => {
  if (!createdInboxId.value) return;
  try {
    await baileysChannel.regenerateQr(createdInboxId.value);
    qrDataUrl.value = null;
  } catch (err) {
    useAlert(err.response?.data?.message || t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_REGEN_FAILED'));
  }
};

const handleFinish = () => {
  stopPolling();
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdInboxId.value },
  });
};

const handleCancel = () => {
  stopPolling();
  router.replace({ name: route.name, params: route.params, query: {} });
};

onMounted(() => {
  // No-op
});

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- STEP 1: Form -->
    <div v-if="step === STEP_FORM">
      <div class="mb-6">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.SETUP_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.SETUP_DESCRIPTION') }}
        </p>
      </div>

      <!-- Risk advisory -->
      <div class="flex items-start gap-2 p-3 mb-5 rounded-lg bg-n-amber-3 border border-n-amber-7 text-n-amber-12 text-sm">
        <span class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0" />
        <div>
          <strong>{{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.RISK_TITLE') }}</strong>
          <p class="mt-0.5 leading-relaxed">{{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.RISK_BODY') }}</p>
        </div>
      </div>

      <form class="space-y-5" @submit.prevent="handleCreate">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.NAME_LABEL') }}
          </label>
          <input
            v-model="inboxName"
            type="text"
            class="block w-full rounded-md border-0 px-3.5 py-2.5 shadow-sm ring-1 ring-inset ring-n-strong text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-inset focus:ring-n-brand sm:text-sm bg-n-background outline-none"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.NAME_PLACEHOLDER')"
            required
          />
          <p class="mt-1.5 text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.NAME_HELP') }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <NextButton type="submit" :is-loading="isCreating" :disabled="!inboxName.trim()">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CONTINUE_BUTTON') }}
          </NextButton>
          <NextButton ghost type="button" @click="handleCancel">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
        </div>
      </form>
    </div>

    <!-- STEP 2: QR Scan -->
    <div v-else-if="step === STEP_QR">
      <div class="mb-6">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_DESCRIPTION') }}
        </p>
      </div>

      <div class="flex flex-col items-center gap-4 p-6 rounded-2xl bg-n-background border border-n-weak">
        <div v-if="qrDataUrl" class="relative">
          <img :src="qrDataUrl" alt="QR Code" class="w-64 h-64 rounded-lg shadow-md" />
        </div>
        <div v-else class="w-64 h-64 flex items-center justify-center rounded-lg bg-n-alpha-2">
          <Spinner color-scheme="primary" />
        </div>

        <div class="flex items-center gap-2 text-sm">
          <span
            class="inline-block w-2 h-2 rounded-full"
            :class="{
              'bg-n-amber-9': sessionState === 'connecting' || sessionState === 'qr',
              'bg-n-teal-9': sessionState === 'connected',
              'bg-n-ruby-9': sessionState === 'banned' || sessionState === 'disconnected',
            }"
          />
          <span class="text-n-slate-12 font-medium">{{ sessionStateLabel }}</span>
        </div>

        <ol class="text-sm text-n-slate-11 space-y-1.5 max-w-sm mt-2">
          <li>1. {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_STEP_1') }}</li>
          <li>2. {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_STEP_2') }}</li>
          <li>3. {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_STEP_3') }}</li>
        </ol>

        <div class="flex items-center gap-2 mt-2">
          <NextButton ghost icon="i-lucide-refresh-cw" sm @click="handleRegenerateQr">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.QR_REGENERATE') }}
          </NextButton>
          <NextButton ghost type="button" sm @click="handleCancel">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- STEP 3: Done -->
    <div v-else-if="step === STEP_DONE" class="text-center py-10">
      <div class="mx-auto w-14 h-14 rounded-full bg-n-teal-3 flex items-center justify-center mb-4">
        <span class="i-lucide-check size-8 text-n-teal-11" />
      </div>
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CONNECTED_TITLE') }}
      </h2>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.CONNECTED_DESCRIPTION', { phone: phoneConnected || '-' }) }}
      </p>
      <NextButton class="mt-6" @click="handleFinish">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.BAILEYS.FINISH_BUTTON') }}
      </NextButton>
    </div>
  </div>
</template>
